/**
 * ContentBlock Zod Schemas
 *
 * Runtime validation schemas for all 12 ContentBlock variants
 * plus a discriminated union covering the entire ContentBlock type.
 *
 * Reuses QuestionSchema, TodoSchema, and ImageMediaTypeSchema from common.schema.ts.
 */

import { z } from 'zod';
import {
  ImageMediaTypeSchema,
  QuestionSchema,
  TodoSchema,
} from './common.schema.js';

// =============================================================================
// Shared Enums
// =============================================================================

/**
 * Tool execution status: 'preparing' | 'running' | 'completed' | 'error' | 'interrupted'
 * Reconciled with the TS `ToolStatus` union in @vestra/agent-sdk contract/blocks.ts.
 */
export const ToolStatusSchema = z.enum(['preparing', 'running', 'completed', 'error', 'interrupted']);

/**
 * Icon hint for tool summaries
 */
export const ToolIconHintSchema = z.enum([
  'search',
  'web',
  'file',
  'file-edit',
  'terminal',
  'package',
  'git',
  'agent',
  'api',
  'database',
  'success',
  'error',
  'default',
]);

/**
 * Credit approval status: 'pending' | 'approved' | 'denied' | 'expired'
 */
export const CreditApprovalStatusSchema = z.enum(['pending', 'approved', 'denied', 'expired']);

/**
 * Routine trigger type: 'schedule' | 'webhook' | 'app_event'
 */
export const RoutineTriggerTypeSchema = z.enum(['schedule', 'webhook', 'app_event']);

// =============================================================================
// Nested Object Schemas
// =============================================================================

/**
 * Human-readable summary for tool operations (matches ToolSummary interface)
 */
export const ToolSummarySchema = z.object({
  action: z.string(),
  target: z.string(),
  details: z.string().optional(),
  iconHint: ToolIconHintSchema.optional(),
});

// =============================================================================
// 1. TextBlock
// =============================================================================

export const TextBlockSchema = z.object({
  type: z.literal('text'),
  text: z.string(),
});

// =============================================================================
// 2. ImageBlock
// =============================================================================

export const ImageBlockSchema = z.object({
  type: z.literal('image'),
  data: z.string(),
  mediaType: ImageMediaTypeSchema,
  // Media-refs S2 — a parse must not STRIP the CDN url or ref fields (zod drops
  // unknown keys): url is what every renderer prefers over base64.
  url: z.string().optional(),
  filename: z.string().optional(),
  sizeBytes: z.number().optional(),
  contentSha: z.string().optional(),
  originTaskId: z.string().optional(),
  fileId: z.string().optional(),
});

// =============================================================================
// 3. ThinkingBlock
// =============================================================================

export const ThinkingBlockSchema = z.object({
  type: z.literal('thinking'),
  thinking: z.string(),
  collapsed: z.boolean().optional(),
  durationMs: z.number().optional(),
  _startedAt: z.number().optional(),
  // Verbatim extended-thinking signature — load-bearing for multi-turn tool use
  // (the next request must replay thinking+signature). Additive + optional.
  signature: z.string().optional(),
});

// =============================================================================
// 4. ToolUseBlock (most complex — uses z.unknown() for deeply nested display objects)
// =============================================================================

export const ToolUseBlockSchema = z.object({
  type: z.literal('tool_use'),
  toolUseId: z.string(),
  toolName: z.string(),
  toolInput: z.unknown(),
  status: ToolStatusSchema,
  result: z.unknown().optional(),
  isError: z.boolean().optional(),
  startTime: z.union([z.date(), z.string()]),
  endTime: z.union([z.date(), z.string()]).optional(),
  summary: ToolSummarySchema.optional(),
  resultSummary: ToolSummarySchema.optional(),
  subagentOutput: z.string().optional(),
  subagentType: z.string().optional(),
  /** Server-enriched display metadata — z.unknown() to avoid over-constraining */
  display: z.unknown().optional(),
  /** Server-enriched result display — z.unknown() to avoid over-constraining */
  resultDisplay: z.unknown().optional(),
  permissionPending: z.boolean().optional(),
  userAnswers: z.record(z.string(), z.string()).optional(),
});

// =============================================================================
// 5. ToolResultBlock
// =============================================================================

export const ToolResultBlockSchema = z.object({
  type: z.literal('tool_result'),
  toolUseId: z.string(),
  content: z.unknown(),
  isError: z.boolean().optional(),
});

// =============================================================================
// 6. QuestionBlock (reuses QuestionSchema from common.schema.ts)
// =============================================================================

export const QuestionBlockSchema = z.object({
  type: z.literal('question'),
  toolUseId: z.string(),
  questions: z.array(QuestionSchema),
  answered: z.boolean(),
  answers: z.record(z.string(), z.string()).optional(),
});

// =============================================================================
// 7. TodoBlock (reuses TodoSchema from common.schema.ts)
// =============================================================================

export const TodoBlockSchema = z.object({
  type: z.literal('todo'),
  todos: z.array(TodoSchema),
});

// =============================================================================
// 8. ErrorBlock
// =============================================================================

export const ErrorCategorySchema = z.enum([
  'error_max_turns',
  'error_max_budget_usd',
  'credit_exhausted',
  'api_credit_error',
  'rate_limited',
  'context_too_long',
  'auth_error',
  'structured_output_error',
  'empty_response',
  'generic',
]);

export type ErrorCategory = z.infer<typeof ErrorCategorySchema>;

// Terminal-reason outcome — lazy-imported to avoid schema-file load-order issues.
import { TurnOutcomeSchema as _TurnOutcomeSchema } from './turn-outcome.schema.js';

export const ErrorBlockSchema = z.object({
  type: z.literal('error'),
  error: z.string(),
  code: z.string().optional(),
  category: ErrorCategorySchema.optional(),
  /**
   * Structured TurnOutcome from classifyTermination(). When present, UI
   * consumers switch on `outcome.kind` — legacy `category` is fallback.
   */
  outcome: _TurnOutcomeSchema.optional(),
});

// =============================================================================
// 9. FileAttachmentBlock
// =============================================================================

export const FileAttachmentBlockSchema = z.object({
  type: z.literal('file_attachment'),
  filename: z.string(),
  url: z.string(),
  mediaType: z.string(),
  sizeBytes: z.number(),
});

// =============================================================================
// 9b. MediaBlock
// =============================================================================

export const MediaBlockSchema = z.object({
  type: z.literal('media'),
  kind: z.enum(['video', 'audio', 'document']),
  filename: z.string(),
  path: z.string(),
  url: z.string().optional(),
  mediaType: z.string(),
  sizeBytes: z.number(),
  contentSha: z.string().optional(),
});

// =============================================================================
// 10. CreditApprovalBlock
// =============================================================================

export const CreditApprovalBlockSchema = z.object({
  type: z.literal('credit_approval'),
  requestId: z.string(),
  toolName: z.string(),
  displayName: z.string(),
  estimatedCost: z.number(),
  currentBalance: z.number(),
  status: CreditApprovalStatusSchema,
});

// =============================================================================
// 11. RoutineConfirmationBlock
// =============================================================================

export const RoutineConfirmationBlockSchema = z.object({
  type: z.literal('routine_confirmation'),
  routineId: z.string(),
  name: z.string(),
  triggerType: RoutineTriggerTypeSchema,
  triggerLabel: z.string(),
  prompt: z.string(),
  agentSlug: z.string().nullable(),
  enabled: z.boolean(),
  // Mirrors the agent-sdk RoutineConfirmationBlock contract — z.object STRIPS
  // unknown keys, so any field the envelope carries must be declared here or a
  // parse silently drops it. armReason is the human detail string, not the
  // RoutineArmReason enum. All optional: schedule/webhook envelopes carry no
  // arm keys at all.
  routineKind: z.enum(['scheduled', 'recurring']).optional(),
  scheduledAt: z.string().nullable().optional(),
  armStatus: z.enum(['armed', 'pending_auth', 'error']).optional(),
  armReason: z.string().optional(),
  // Blast radius of an event routine (composeScopeSummary): the kind and the one
  // plain sentence the card shows. Also an optional pair — schedule routines and
  // pre-declaration rows carry neither.
  scopeKind: z.enum(['all', 'filtered', 'semantic', 'filtered_semantic']).optional(),
  scopeSummary: z.string().optional(),
});

// =============================================================================
// 11b. ItemConfirmationBlock
// =============================================================================

export const ItemTypeSchema = z.enum(['skill', 'agent', 'ability']);

export const ItemConfirmationBlockSchema = z.object({
  type: z.literal('item_confirmation'),
  itemType: ItemTypeSchema,
  itemId: z.string(),
  slug: z.string(),
  name: z.string(),
  icon: z.string().optional(),
  color: z.string().optional(),
  abilitySlug: z.string(),
});

// =============================================================================
// 12. TurnFooterBlock
// =============================================================================

export const TurnFooterBlockSchema = z.object({
  type: z.literal('turn_footer'),
  durationMs: z.number(),
  interrupted: z.boolean(),
  interruptReason: z.enum(['user', 'disconnected', 'credits_exhausted', 'lead', 'restart', 'restart_paused']).optional(),
  word: z.string().optional(),
  stopReason: z.string().optional(),
  creditCost: z.number().nonnegative().optional(),
  terminalReason: z
    .object({
      status: z.enum(['ok', 'truncated', 'stopped', 'paused', 'failed']),
      label: z.string(),
      kind: z.string(),
    })
    .optional(),
  planLimit: z.literal('switched').optional(),
  planLimitNetAt: z.string().optional(),
});

export const CompactionBlockSchema = z.object({
  type: z.literal('compaction'),
  trigger: z.enum(['manual', 'auto']),
  summary: z.string(),
});

// =============================================================================
// 13. NoOutputBlock — client-synthesized "the model returned nothing" affordance
// =============================================================================

export const NoOutputBlockSchema = z.object({
  type: z.literal('no_output'),
});

// =============================================================================
// 14. MemoryRecallBlock — the pages a turn started with (Nami P4 T2)
// =============================================================================

/**
 * Minted by the server from the sandbox's AGENT_MEMORY_RECALL frame so the
 * "From your brain" pill survives a reload. `path` is what the brain producer
 * sent (tree-relative for the tree consult, `knowledge/self/goals.md`); bodies
 * never ride here.
 */
export const MemoryRecallBlockSchema = z.object({
  type: z.literal('memory_recall'),
  mode: z.enum(['select', 'synthesize']),
  memories: z.array(
    z.object({
      path: z.string(),
      scope: z.enum(['personal', 'team']),
      content: z.string().optional(),
    }),
  ),
});

// =============================================================================
// 15. NoticeBlock — one quiet line in the task (engines T0)
// =============================================================================

/** The most a notice may say. One line; the composers clip to this. */
export const NOTICE_TEXT_MAX = 500;

/**
 * Minted by the server from a buffered TASK_NOTICE: a step the gate refused (composed by the
 * relay from PERMISSION_DENIED) or the runner's plan heads-up. `ref` is its identity in a task.
 */
export const NoticeBlockSchema = z.object({
  type: z.literal('notice'),
  tone: z.enum(['quiet', 'gold']),
  text: z.string().min(1).max(NOTICE_TEXT_MAX),
  ref: z.string().min(1).max(200),
});

// =============================================================================
// ContentBlock Discriminated Union
// =============================================================================

export const ContentBlockSchema = z.discriminatedUnion('type', [
  TextBlockSchema,
  ImageBlockSchema,
  ThinkingBlockSchema,
  ToolUseBlockSchema,
  ToolResultBlockSchema,
  QuestionBlockSchema,
  TodoBlockSchema,
  ErrorBlockSchema,
  FileAttachmentBlockSchema,
  MediaBlockSchema,
  CreditApprovalBlockSchema,
  RoutineConfirmationBlockSchema,
  ItemConfirmationBlockSchema,
  TurnFooterBlockSchema,
  CompactionBlockSchema,
  MemoryRecallBlockSchema,
  NoticeBlockSchema,
  NoOutputBlockSchema,
]);

// =============================================================================
// Type Exports (inferred from schemas)
// =============================================================================

export type TextBlockInput = z.input<typeof TextBlockSchema>;
export type ImageBlockInput = z.input<typeof ImageBlockSchema>;
export type ThinkingBlockInput = z.input<typeof ThinkingBlockSchema>;
export type ToolUseBlockInput = z.input<typeof ToolUseBlockSchema>;
export type ToolResultBlockInput = z.input<typeof ToolResultBlockSchema>;
export type QuestionBlockInput = z.input<typeof QuestionBlockSchema>;
export type TodoBlockInput = z.input<typeof TodoBlockSchema>;
export type ErrorBlockInput = z.input<typeof ErrorBlockSchema>;
export type FileAttachmentBlockInput = z.input<typeof FileAttachmentBlockSchema>;
export type MediaBlockInput = z.input<typeof MediaBlockSchema>;
export type CreditApprovalBlockInput = z.input<typeof CreditApprovalBlockSchema>;
export type RoutineConfirmationBlockInput = z.input<typeof RoutineConfirmationBlockSchema>;
export type ItemConfirmationBlockInput = z.input<typeof ItemConfirmationBlockSchema>;
export type TurnFooterBlockInput = z.input<typeof TurnFooterBlockSchema>;
export type MemoryRecallBlockInput = z.input<typeof MemoryRecallBlockSchema>;
export type NoticeBlockInput = z.input<typeof NoticeBlockSchema>;
export type ContentBlockInput = z.input<typeof ContentBlockSchema>;
